import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterForm = () => {
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [planPreSelected, setPlanPreSelected] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    bio: '',
    membership_tier: 'starter',
    billing_period: 'monthly'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { register } = useAuth();

  // Handle pre-selected plan from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const selectedPlan = urlParams.get('plan');
    if (selectedPlan) {
      setFormData(prev => ({
        ...prev,
        membership_tier: selectedPlan
      }));
      setPlanPreSelected(true);
    }
  }, [location]);

  const getMembershipTiers = () => [
    {
      id: 'starter',
      name: 'Starter',
      price: '$47 one time',
      subtitle: 'Grounding + 21-Day Reset',
      description: 'The 21 day reset',
      features: [
        'Grounding foundation lessons',
        'Full 21-Day Reset program',
        'Structured daily reflections',
        'Progress tracking',
        'Community access during program'
      ],
      footer: 'For those who want structure without long-term commitment.',
      color: 'var(--primary)'
    },
    {
      id: 'achiever',
      name: 'Achiever',
      price: formData.billing_period === 'yearly' ? '$199/year' : '$19.99/month',
      priceNote: formData.billing_period === 'yearly' ? 'Save $39.88' : '',
      subtitle: 'Full ecosystem access.',
      description: 'Includes Grounding, 21-Day Reset, and full access to Evolve 90 (self-paced).',
      featuresLabel: 'Includes:',
      features: [
        'Full access to Evolve 90',
        'Grounding + 21-Day Reset',
        'Complete lesson library',
        'Daily structured integration',
        'Habit and goal tracking',
        'Full community participation',
        'Ongoing integration support'
      ],
      footer: 'For members building consistency long-term.',
      color: 'var(--achiever-purple)'
    },
    {
      id: 'creator',
      name: 'Creator',
      price: formData.billing_period === 'yearly' ? '$499.99/year' : '$49/month',
      priceNote: formData.billing_period === 'yearly' ? 'Save $88.01' : '',
      subtitle: 'Step into leadership and guide others.',
      description: 'Everything in Achiever plus:',
      features: [
        'Lead groups and discussions',
        'Create and publish courses',
        'Recognition badge',
        'Priority support'
      ],
      footer: 'For those ready to lead with stability and integrity.',
      color: 'var(--warm-gold)'
    }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleNext = () => {
    if (step === 1) {
      if (!formData.name || !formData.email || !formData.password) {
        setError('Please fill in all required fields');
        return;
      }
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }
      setError('');
      setStep(2);
    } else if (step === 2) {
      // If plan was pre-selected from /membership, skip step 3 and submit directly
      if (planPreSelected) {
        // Trigger form submission
        submitRegistration();
      } else {
        setStep(3);
      }
    }
  };

  const submitRegistration = async () => {
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, formData);
      
      if (response.data.payment_required) {
        // User selected a paid tier - redirect to payment
        const registrationId = response.data.registration_id;
        
        // Create checkout session for registration
        const checkoutResponse = await axios.post(`${BACKEND_URL}/api/payments/checkout/registration`, null, {
          params: { 
            registration_id: registrationId,
            billing_period: formData.billing_period 
          }
        });
        
        if (checkoutResponse.data.url) {
          // Redirect to Stripe checkout
          window.location.href = checkoutResponse.data.url;
        } else {
          throw new Error('Failed to create payment session');
        }
      } else {
        // Free tier registration completed
        const result = { success: true };
        
        if (result.success) {
          // Login completed for free tier
          localStorage.setItem('token', response.data.access_token);
          window.location.reload();
        }
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.response?.data?.detail || error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setError('');
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    submitRegistration();
  };

  const renderStep1 = () => (
    <div>
      <h2>Create Your Account</h2>
      <p style={{ textAlign: 'center', marginBottom: '2rem', color: 'var(--text-light)' }}>
        Step 1 of {planPreSelected ? '2' : '3'}: Basic Information
      </p>

      <form>
        <div className="form-group">
          <label htmlFor="name">Full Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            className="form-input"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Enter your full name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            name="email"
            className="form-input"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="Enter your email"
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password *</label>
          <input
            type="password"
            id="password"
            name="password"
            className="form-input"
            value={formData.password}
            onChange={handleChange}
            required
            placeholder="Create a strong password (min 6 characters)"
          />
        </div>

        <button 
          type="button" 
          className="btn-primary"
          onClick={handleNext}
        >
          Next: Tell Us About You
        </button>
      </form>
    </div>
  );

  const renderStep2 = () => {
    const selectedTier = getMembershipTiers().find(t => t.id === formData.membership_tier);
    
    return (
      <div>
        <h2>About You</h2>
        <p style={{ textAlign: 'center', marginBottom: '2rem', color: 'var(--text-light)' }}>
          Step 2 of {planPreSelected ? '2' : '3'}: Optional Information
        </p>

        {/* Show selected plan summary if pre-selected */}
        {planPreSelected && selectedTier && (
          <div style={{
            background: `${selectedTier.color}15`,
            border: `2px solid ${selectedTier.color}`,
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, marginBottom: '0.25rem', color: 'var(--text-dark)', fontWeight: '600' }}>
              Selected Plan: <span style={{ color: selectedTier.color }}>{selectedTier.name}</span> - {selectedTier.price}
            </p>
            {selectedTier.subtitle && (
              <p style={{ margin: 0, color: 'var(--text-light)', fontSize: '0.9rem' }}>{selectedTier.subtitle}</p>
            )}
          </div>
        )}

        <form>
          <div className="form-group">
            <label htmlFor="bio">Bio (Optional)</label>
            <textarea
              id="bio"
              name="bio"
              className="form-textarea"
              value={formData.bio}
              onChange={handleChange}
              placeholder="Tell us about yourself and your goals..."
              rows="4"
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              type="button" 
              className="btn-secondary"
              onClick={handleBack}
            >
              Back
            </button>
            <button 
              type="button" 
              className="btn-primary"
              onClick={handleNext}
              disabled={loading}
              style={{ flex: 1 }}
            >
              {loading ? 'Processing...' : (planPreSelected ? 'Continue to Payment' : 'Next: Choose Your Plan')}
            </button>
          </div>
        </form>
      </div>
    );
  };

  const renderStep3 = () => {
    const membershipTiers = getMembershipTiers();
    
    return (
      <div>
        <h2>Choose Your Membership</h2>
        <p style={{ textAlign: 'center', marginBottom: '1rem', color: 'var(--text-light)' }}>
          Step 3 of 3: Select your plan (you can upgrade anytime)
        </p>
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '2rem', 
          padding: '1rem', 
          background: 'rgba(255, 107, 53, 0.1)', 
          borderRadius: '8px', 
          fontSize: '0.9rem', 
          color: 'var(--text-dark)' 
        }}>
          📧 By creating an account, you'll be subscribed to our newsletter for transformation tips, progress updates, and community insights. You can unsubscribe anytime.
        </div>

        {/* Billing Period Toggle */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ 
            display: 'inline-flex', 
            background: 'var(--soft-ash)', 
            borderRadius: '25px', 
            padding: '4px',
            marginBottom: '1rem'
          }}>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, billing_period: 'monthly' })}
              style={{
                padding: '8px 20px',
                border: 'none',
                borderRadius: '20px',
                background: formData.billing_period === 'monthly' ? 'var(--primary-fire)' : 'transparent',
                color: formData.billing_period === 'monthly' ? 'white' : 'var(--text-dark)',
                fontWeight: formData.billing_period === 'monthly' ? 'bold' : 'normal',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, billing_period: 'yearly' })}
              style={{
                padding: '8px 20px',
                border: 'none',
                borderRadius: '20px',
                background: formData.billing_period === 'yearly' ? 'var(--primary-fire)' : 'transparent',
                color: formData.billing_period === 'yearly' ? 'white' : 'var(--text-dark)',
                fontWeight: formData.billing_period === 'yearly' ? 'bold' : 'normal',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              Yearly
            </button>
          </div>
          {formData.billing_period === 'yearly' && (
            <p style={{ fontSize: '0.9rem', color: 'var(--success-green)', margin: 0 }}>
              💰 Save up to 17% with yearly billing!
            </p>
          )}
        </div>

        <div className="membership-tiers" style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
          {membershipTiers.map((tier) => (
            <div 
              key={tier.id}
              className={`membership-tier ${formData.membership_tier === tier.id ? 'selected' : ''}`}
              onClick={() => setFormData({ ...formData, membership_tier: tier.id })}
              style={{
                border: formData.membership_tier === tier.id ? `3px solid ${tier.color}` : tier.special ? '2px solid #FFD700' : '2px solid var(--soft-ash)',
                borderRadius: '12px',
                padding: '1.5rem',
                cursor: 'pointer',
                backgroundColor: formData.membership_tier === tier.id ? `${tier.color}20` : tier.special ? 'linear-gradient(135deg, #FFD70020, #FFA50010)' : 'var(--pure-white)',
                background: tier.special ? 'linear-gradient(135deg, #FFD70020, #FFA50010)' : formData.membership_tier === tier.id ? `${tier.color}20` : 'var(--pure-white)',
                transition: 'all 0.3s ease',
                position: 'relative',
                boxShadow: tier.special ? '0 4px 15px rgba(255, 215, 0, 0.3)' : 'none'
              }}
            >
              {tier.special && (
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '1rem',
                  background: '#FFD700',
                  color: '#8B4513',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  fontWeight: 'bold'
                }}>
                  👑 LIMITED OFFER
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ color: tier.color, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {tier.special && '👑'} {tier.name}
                </h3>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: tier.color }}>
                    {tier.price}
                  </div>
                  {tier.priceNote && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--success-green)', fontWeight: 'bold' }}>
                      {tier.priceNote}
                    </div>
                  )}
                </div>
              </div>
              {tier.subtitle && (
                <p style={{ marginBottom: '0.5rem', color: 'var(--text-dark)', fontWeight: '500' }}>{tier.subtitle}</p>
              )}
              <p style={{ marginBottom: '1rem', color: 'var(--text-light)', fontSize: '0.9rem' }}>{tier.description}</p>
              {tier.featuresLabel && (
                <p style={{ marginBottom: '0.5rem', color: 'var(--text-dark)', fontWeight: '600', fontSize: '0.9rem' }}>{tier.featuresLabel}</p>
              )}
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {tier.features.map((feature, index) => (
                  <li key={index} style={{ marginBottom: '0.5rem', color: 'var(--text-dark)' }}>
                    ✓ {feature}
                  </li>
                ))}
              </ul>
              {tier.footer && (
                <p style={{ marginTop: '1rem', color: 'var(--text-light)', fontSize: '0.85rem', fontStyle: 'italic' }}>{tier.footer}</p>
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              type="button" 
              className="btn-secondary"
              onClick={handleBack}
            >
              Back
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading}
              style={{ flex: 1 }}
            >
              {loading ? 'Creating Account...' : `Create Account with ${membershipTiers.find(t => t.id === formData.membership_tier)?.name}`}
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <div className="auth-form">
      <div className="phoenix-logo" style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <img 
          src="/logo.webp" 
          alt="Zero Give Up" 
          style={{ maxWidth: '200px', height: 'auto' }}
        />
      </div>
      
      {error && <div className="error-message">{error}</div>}

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}

      <div className="auth-switch" style={{ marginTop: '2rem' }}>
        Already have an account? <Link to="/auth/login">Sign in here</Link>
      </div>
    </div>
  );
};

export default RegisterForm;